"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ChildSelect from "./ChildSelect";
import DateRangePicker from "./DateRangePicker";
import GenericSelectionModal from "@/components/GenericSelectionModal";
import { defaultHotelGuestConfig } from "@/configs/hotel/search";
import { hotelGuestDisplayFormatter } from "@/configs/hotel/search/guestConfig";
import { useLoading } from "@/contexts/LoadingContext";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useHotelRoomSearch } from "@/hooks/useHotelRoomSearch";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname } from "@/i18n/navigation";
import { formatDate, isBefore, isSameDay } from "@/utils/dateFormatter";
import { getLocalizedPath } from "@/utils/pathUtils";

// URL'leri normalize eden helper fonksiyon (query parametrelerinin sırasını normalize eder)
const normalizeUrl = (urlStr) => {
  try {
    // Eğer relative URL ise, base URL ekle
    const baseUrl =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost";
    const fullUrl = urlStr.startsWith("http") ? urlStr : `${baseUrl}${urlStr}`;
    const urlObj = new URL(fullUrl);
    // Pathname'i normalize et (trailing slash'i kaldır, decode et)
    let path = urlObj.pathname;
    // Trailing slash'i kaldır (eğer root değilse)
    if (path.length > 1 && path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    // Decode et (URL encoding'i kaldır)
    path = decodeURIComponent(path);

    const params = new URLSearchParams(urlObj.search);
    // Query parametrelerini sırala (sıra farklı olabilir)
    const sortedParams = Array.from(params.keys())
      .sort()
      .map((key) => `${key}=${params.get(key)}`)
      .join("&");
    return `${path}${sortedParams ? `?${sortedParams}` : ""}`;
  } catch (error) {
    // Hata durumunda orijinal URL'i döndür
    void error;
    return urlStr;
  }
};

const ROOM_SEARCH_SCROLL_PARAM = "scroll_to_rooms";

const Form = forwardRef(
  (
    {
      initialLocationSlug,
      initialCheckinDate,
      initialCheckoutDate,
      initialGuests,
    } = {},
    ref,
  ) => {
    const t = useTranslationDetector("hotelSearch");
    const tForm = useTranslationDetector();
    const locale = useLocale();
    const searchParams = useSearchParams();
    const { show } = useLoading();

    // Initial data objesini oluştur (flight search ile aynı mantık)
    const initialData = {};
    if (initialCheckinDate) initialData.checkinDate = initialCheckinDate;
    if (initialCheckinDate) initialData.checkoutDate = initialCheckoutDate;
    if (initialGuests) initialData.guests = initialGuests;

    const {
      checkinDate,
      checkoutDate,
      guests,
      setCheckinDate,
      setCheckoutDate,
      setGuests,
      publicHolidays,
      validateForm,
      generateHotelRoomSearchUrl,
      refreshPublicHolidays,
    } = useHotelRoomSearch(initialData, initialLocationSlug);

    const isMobile = useIsMobile();
    const guestsModalRef = useRef(null);
    const dateSelectionRef = useRef(null);
    const lastDatesRef = useRef({
      checkin: initialCheckinDate || null,
      checkout: initialCheckoutDate || null,
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const { warningModal } = useModalHelpers();
    const hasShownChildrenAgesModalRef = useRef(false);
    const lastGuestChangeRef = useRef(null);

    const openGuestsModal = useCallback(() => {
      guestsModalRef.current?.open();
    }, []);

    const closeGuestsModal = useCallback(() => {
      guestsModalRef.current?.close();
    }, []);

    // Çocuk yaşı seçilmemişse önce konuk seçimini kapat, uyarı göster; uyarı kapatılınca konuk seçimini tekrar aç
    useEffect(() => {
      const shouldShow =
        validationErrors.childrenAges &&
        Object.keys(validationErrors).length === 1;

      if (shouldShow && !hasShownChildrenAgesModalRef.current) {
        hasShownChildrenAgesModalRef.current = true;
        closeGuestsModal();
        warningModal({
          title: t("childrenAgesRequiredTitle"),
          message: t("childrenAgesRequiredMessage"),
          isBottomSheet: true,
          onClose: openGuestsModal,
        });
      } else if (!shouldShow) {
        hasShownChildrenAgesModalRef.current = false;
      }
      // t ve warningModal değişebilir, sonsuz döngüyü önlemek için sadece validationErrors
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationErrors]);

    // Ref ile dışarıdan erişilebilir method'ları expose et
    useImperativeHandle(ref, () => ({
      openDatePicker: () => {
        dateSelectionRef.current?.open();
      },
    }));

    // Herhangi bir portal açıksa body scroll'u kilitle
    const shouldLockBodyScroll = isMobile && isDatePickerOpen;
    useLockBodyScroll(shouldLockBodyScroll);

    const guestConfig = useMemo(() => {
      return defaultHotelGuestConfig;
    }, []);

    const handleApplyValidationError = useCallback(() => {
      closeGuestsModal();
      warningModal({
        title: t("childrenAgesRequiredTitle"),
        message: t("childrenAgesRequiredMessage"),
        isBottomSheet: true,
        onClose: openGuestsModal,
      });
    }, [warningModal, t, openGuestsModal, closeGuestsModal]);

    const validateAndSearch = useCallback(() => {
      const errors = validateForm();

      if (Object.keys(errors).length === 0) {
        // Validasyon başarılı - hatalar varsa temizle
        setValidationErrors({});

        // URL oluştur ve yönlendir
        const url = generateHotelRoomSearchUrl();

        if (url) {
          const nextUrl = new URL(url, window.location.origin);
          nextUrl.searchParams.set(ROOM_SEARCH_SCROLL_PARAM, "1");
          const urlWithScrollIntent = `${nextUrl.pathname}${nextUrl.search}`;

          // Mevcut URL'i oluştur (pathname dynamic route olduğu için initialLocationSlug kullan)
          const basePath = getLocalizedPath("/hotel", locale);
          const currentBaseUrl = `${basePath}/${initialLocationSlug}`;
          const currentUrl = `${currentBaseUrl}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

          // URL'leri normalize et ve karşılaştır
          const normalizedCurrent = normalizeUrl(currentUrl);
          const normalizedNew = normalizeUrl(urlWithScrollIntent);

          // URL aynıysa search atma (loader açma ve navigation yapma)
          if (normalizedCurrent === normalizedNew) {
            return;
          }

          show();
          const localizedPath = getPathname({
            href: urlWithScrollIntent,
            locale,
          });
          window.location.href = localizedPath;
        }
      } else {
        // Validasyon başarısız - hataları göster
        setValidationErrors(errors);
      }
    }, [
      validateForm,
      generateHotelRoomSearchUrl,
      show,
      initialLocationSlug,
      locale,
      searchParams,
    ]);

    const handleGuestSelect = useCallback(
      (values) => {
        // Çocuk varsa yaş seçimi zorunlu - Tamam'a basmadan önce kontrol
        if (
          values.child > 0 &&
          (values.childrenAges?.length || 0) !== values.child
        ) {
          setValidationErrors({ childrenAges: true });
          // Seçilen değerleri state'e yaz (konuk sayısı kaybolmasın)
          setGuests(values);
          return;
        }

        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next.childrenAges;
          return next;
        });
        setGuests(values);

        // Konuk sayısı seçildikten sonra (Uygula butonuna basıldığında) arama yap
        // Sadece tarihler de seçilmişse arama yap
        if (checkinDate && checkoutDate) {
          // State güncellenmesi ve modal kapanması için gecikme
          setTimeout(() => {
            // Güncel values parametresini kullanarak URL oluştur
            const checkinDateStr = formatDate(checkinDate, {
              format: "YYYY-MM-DD",
            });
            const checkoutDateStr = formatDate(checkoutDate, {
              format: "YYYY-MM-DD",
            });

            // Query parametreleri oluştur
            const queryParams = new URLSearchParams();

            // Tarihleri query parametrelerine ekle
            if (checkinDateStr) {
              queryParams.set("check_in", checkinDateStr);
            }
            if (checkoutDateStr) {
              queryParams.set("check_out", checkoutDateStr);
            }

            // Misafir bilgilerini ekle (hotel formatı: adult_1, child_1, childage_1_1, childage_1_2, ...)
            if (values.adult > 0) {
              queryParams.set("adult_1", values.adult.toString());
            }

            if (values.child > 0) {
              queryParams.set("child_1", values.child.toString());

              // Çocuk yaşlarını childage_1_1, childage_1_2, ... formatında ekle
              const childrenAges = values.childrenAges || [];
              childrenAges.forEach((age, index) => {
                queryParams.set(`childage_1_${index + 1}`, age.toString());
              });
            }
            queryParams.set(ROOM_SEARCH_SCROLL_PARAM, "1");

            const queryString = queryParams.toString();
            // URL formatı: /otel/{hotelSlug}?check_in=...&check_out=...&adult_1=...&child_1=...&childage_1_1=...
            const basePath = getLocalizedPath("/hotel", locale);
            const baseUrl = `${basePath}/${initialLocationSlug}`;
            const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

            if (url) {
              // Mevcut URL'i oluştur (pathname dynamic route olduğu için initialLocationSlug kullan)
              const currentBasePath = getLocalizedPath("/hotel", locale);
              const currentBaseUrl = `${currentBasePath}/${initialLocationSlug}`;
              const currentUrl = `${currentBaseUrl}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

              // URL'leri normalize et ve karşılaştır
              const normalizedCurrent = normalizeUrl(currentUrl);
              const normalizedNew = normalizeUrl(url);

              // URL aynıysa search atma (loader açma ve navigation yapma)
              if (normalizedCurrent === normalizedNew) {
                return;
              }

              show();
              const localizedPath = getPathname({ href: url, locale });
              window.location.href = localizedPath;
            }
          }, 200);
        }
      },
      [
        setGuests,
        checkinDate,
        checkoutDate,
        initialLocationSlug,
        locale,
        show,
        searchParams,
      ],
    );

    const handleGuestChange = useCallback(
      (values) => {
        const nextStr = JSON.stringify(values || {});
        if (lastGuestChangeRef.current === nextStr) return;
        lastGuestChangeRef.current = nextStr;
        setGuests(values);
      },
      [setGuests],
    );

    const onSelectCheckoutDate = useCallback(
      (date, triggerDate) => {
        const clickedDate = triggerDate || date?.from;
        if (!clickedDate) return;

        // Giriş/çıkış hangi inputtan açılmış olursa olsun: ilk tıklanan tarih giriş, ikinci çıkış.

        // Edge case: Her iki tarih de dolu ve kullanıcı mevcut giriş veya çıkış gününe tıkladı → yeni seçim başlat
        if (
          checkinDate &&
          checkoutDate &&
          (isSameDay(clickedDate, checkinDate) ||
            isSameDay(clickedDate, checkoutDate))
        ) {
          setCheckinDate(clickedDate);
          setCheckoutDate(null);
          return;
        }

        let willHaveCheckout = false;

        if (!checkinDate) {
          setCheckinDate(clickedDate);
          setCheckoutDate(null);
        } else if (!checkoutDate) {
          if (isBefore(clickedDate, checkinDate)) {
            setCheckinDate(clickedDate);
            setCheckoutDate(null);
          } else if (isSameDay(clickedDate, checkinDate)) {
            const nextDay = new Date(checkinDate);
            nextDay.setDate(nextDay.getDate() + 1);
            setCheckoutDate(nextDay);
            willHaveCheckout = true;
            if (!isMobile) dateSelectionRef.current?.close();
          } else {
            setCheckoutDate(clickedDate);
            willHaveCheckout = true;
            if (!isMobile) dateSelectionRef.current?.close();
          }
        } else {
          setCheckinDate(clickedDate);
          setCheckoutDate(null);
        }

        // Çıkış da seçildiyse konuk modal'ını aç (tarih seçimi tamamlandığında)
        if (willHaveCheckout) {
          setTimeout(() => {
            guestsModalRef.current?.open();
          }, 300);
        }
      },
      [setCheckoutDate, setCheckinDate, checkinDate, checkoutDate, isMobile],
    );

    const handleDateOpen = useCallback(() => {
      // Tarih seçici açılırken mevcut değerleri snapshot olarak sakla
      lastDatesRef.current = {
        checkin: checkinDate,
        checkout: checkoutDate,
      };
      refreshPublicHolidays();
    }, [checkinDate, checkoutDate, refreshPublicHolidays]);

    const handleDateCancel = useCallback(() => {
      // Çarpıya basıldığında, snapshot alınan değerlere geri dön
      const { checkin, checkout } = lastDatesRef.current || {};
      setCheckinDate(checkin || null);
      setCheckoutDate(checkout || null);
    }, [setCheckinDate, setCheckoutDate]);

    return (
      <div className="flex flex-col gap-4 md:gap-5">
        <div className="shadow-medium flex flex-col gap-0 rounded-xl border-solid border-[#C9C8E3] bg-white max-md:border md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
          <div className="flex w-full flex-row items-center md:gap-4">
            <div className="md:shadow-medium flex w-full flex-col items-center rounded-lg shadow-none md:w-4/4 md:flex-row md:bg-white">
              {/* Date Selection - Roundtrip */}
              <div className="flex w-full items-center">
                <div className="w-full">
                  <DateRangePicker
                    ref={dateSelectionRef}
                    id="hotel-date"
                    label={tForm("hotel.dateSelection")}
                    onSelect={onSelectCheckoutDate}
                    value={{
                      from: checkinDate,
                      to: checkoutDate,
                    }}
                    disabled={checkinDate}
                    onPortalStateChange={setIsDatePickerOpen}
                    defaultMonth={checkinDate}
                    checkinDate={checkinDate}
                    checkoutDate={checkoutDate}
                    publicHolidays={publicHolidays}
                    error={
                      validationErrors.checkinDate ||
                      validationErrors.checkoutDate
                    }
                    containerClassName="!border-b-0"
                    onOpen={handleDateOpen}
                    onCancel={handleDateCancel}
                    scrollToForm
                  />
                </div>
              </div>
            </div>

            <div className="bg-border h-4 w-[1px] md:hidden" />

            {/* Guest Selection */}
            <div className="w-3/5 pl-3 md:w-1/4 md:max-w-[200px]">
              <GenericSelectionModal
                ref={guestsModalRef}
                config={guestConfig}
                label={tForm("hotelSearch.guestTitle")}
                placeholder={tForm("hotelSearch.guestSelect")}
                value={guests}
                onSelect={handleGuestSelect}
                onChange={handleGuestChange}
                onApplyValidationError={handleApplyValidationError}
                displayFormatter={(values) =>
                  hotelGuestDisplayFormatter(values, t)
                }
                applyButtonText={tForm("common.apply")}
                renderFooter={(props) => (
                  <ChildSelect
                    childCount={props.tempValues.child}
                    tempValues={props.tempValues}
                    setTempValues={props.setTempValues}
                  />
                )}
                className="max-md:[&_.selection-container]:h-[48px] max-md:[&_.selection-container]:border-none max-md:[&_.selection-container>button]:p-0 [&_.selection-footer]:border-none max-md:[&_.selection-label]:hidden"
              />
            </div>

            <div className="flex items-center justify-center px-4 md:hidden">
              <button type="button" onClick={validateAndSearch}>
                <FontAwesomeIcon
                  className="h-6 w-6"
                  icon={byPrefixAndName.far["magnifying-glass"]}
                />
              </button>
            </div>

            {/* Search Button */}
            <div className="hidden p-3 md:block md:p-0">
              <button
                type="button"
                onClick={validateAndSearch}
                className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[66px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 font-semibold text-white transition-all duration-200 ease-in-out md:w-max lg:text-lg"
              >
                {/* Yazı: Mobile'de gizle, Desktop'ta göster */}
                <span className="whitespace-nowrap">
                  {tForm("hotel.searchRoomButton")}
                </span>

                {/* Turna icon: Mobile'de gizle, Desktop'ta göster */}
                <FontAwesomeIcon
                  className="text-[28px]"
                  icon={byPrefixAndName.fak["turna-logo-icon"]}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

Form.displayName = "Form";

export default Form;
